import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Checkbox,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  ChatBubble as ChatBubbleIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  EmailOutlined as EmailOutlinedIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  Campaign as CampaignIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import { smartleadAPI, voiceAgentAPI, linkedinAPI } from "../services/api";

// Candidate Card Component
const CandidateCard = ({
  candidate,
  checked,
  onCheckboxChange,
  onPhoneClick,
  onCardClick,
  showCheckbox = false,
}) => {
  return (
    <Card
      onClick={() => onCardClick?.(candidate)}
      sx={{
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        mb: 2,
        position: "relative",
        cursor: "pointer",
        "&:hover": {
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <CardContent
        sx={{
          padding: "16px",
          position: "relative",
        }}
      >
        {showCheckbox && (
          <Checkbox
            checked={checked || false}
            onChange={(e) => {
              e.stopPropagation();
              onCheckboxChange?.(candidate.apolloId, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: "8px",
              left: "8px",
              padding: 0,
              zIndex: 2,
            }}
          />
        )}
        {/* Phone Icon in right corner */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log("Phone icon clicked", candidate);
            if (onPhoneClick) {
              onPhoneClick(candidate);
            } else {
              console.error("onPhoneClick handler not provided");
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            padding: "4px",
            zIndex: 2,
            color: "#3B82F6",
            "&:hover": {
              backgroundColor: "#EFF6FF",
              color: "#2563EB",
            },
            cursor: "pointer",
          }}
        >
          <PhoneIcon sx={{ fontSize: "18px" }} />
        </IconButton>
        <Box
          sx={{
            paddingLeft: showCheckbox ? "32px" : 0,
            paddingRight: "32px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: "#101828",
              mb: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {candidate.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6B7280",
              fontSize: "14px",
              mb: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {candidate.title}
          </Typography>
          <Chip
            label={candidate.batch}
            size="small"
            sx={{
              backgroundColor: "#EFF6FF",
              color: "#155DFC",
              fontWeight: 500,
              fontSize: "12px",
              height: "20px",
              mb: candidate.matchScore ? 1.5 : 0,
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
          {candidate.matchScore && (
            <Box sx={{ mt: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#6B7280", fontSize: "12px" }}
                >
                  Match Score
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#3B82F6",
                    fontSize: "12px",
                  }}
                >
                  {candidate.matchScore}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={candidate.matchScore}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#E5E7EB",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#3B82F6",
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          )}
          {candidate.email && (
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                fontSize: "12px",
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: "14px" }} />
              {candidate.email}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Pipeline Column Component
const PipelineColumn = ({
  title,
  icon,
  color,
  candidates,
  id,
  showCheckbox,
  selectedApolloIds,
  onCheckboxChange,
  onPhoneClick,
  onCardClick,
}) => {
  const IconComponent = icon;

  return (
    <Box
      sx={{
        minWidth: { xs: "240px", sm: "280px" },
        width: { xs: "240px", sm: "280px" },
        backgroundColor: "#F9FAFB",
        borderRadius: "12px",
        padding: { xs: "12px", sm: "16px" },
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        minHeight: "400px",
        flexShrink: 0,
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          padding: "12px",
          backgroundColor: color,
          borderRadius: "8px",
        }}
      >
        <IconComponent sx={{ fontSize: "20px", color: "#FFFFFF" }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#FFFFFF",
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <Chip
          label={candidates.length}
          size="small"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: "12px",
            height: "24px",
          }}
        />
      </Box>

      {/* Decision Maker Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          flex: 1,
          minHeight: "200px",
        }}
      >
        {candidates.length > 0 ? (
          candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              showCheckbox={showCheckbox}
              checked={selectedApolloIds?.includes(candidate.apolloId)}
              onCheckboxChange={onCheckboxChange}
              onPhoneClick={onPhoneClick}
              onCardClick={onCardClick}
            />
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100px",
              color: "#9CA3AF",
              fontSize: "14px",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            No decision makers in this stage
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Pipeline Summary Card Component
const SummaryCard = ({ icon, count, label, color }) => {
  const IconComponent = icon;

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <IconComponent
          sx={{
            fontSize: "32px",
            color: color,
            mb: 1,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: "32px",
            color: "#101828",
            mb: 0.5,
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#6B7280",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Card Details Modal
const CardDetailsModal = ({
  open,
  onClose,
  candidate,
  onSave,
  isNew = false,
  viewOnly = false,
}) => {
  if (!candidate && !isNew) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          fontWeight: 600,
        }}
      >
        Card Details
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
          {candidate?.name && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Name
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#101828",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                {candidate.name}
              </Typography>
            </Box>
          )}

          {candidate?.title && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Title
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#101828",
                  fontSize: "15px",
                }}
              >
                {candidate.title}
              </Typography>
            </Box>
          )}

          {(candidate?.batch || candidate?.domain) && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Company / Batch
              </Typography>
              <Chip
                label={candidate.batch || candidate.domain}
                size="small"
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: "#155DFC",
                  fontWeight: 500,
                  fontSize: "13px",
                  height: "24px",
                }}
              />
            </Box>
          )}

          {candidate?.email && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Email
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailOutlinedIcon
                  sx={{ fontSize: "18px", color: "#6B7280" }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#101828",
                    fontSize: "15px",
                  }}
                >
                  {candidate.email}
                </Typography>
              </Box>
            </Box>
          )}

          {(candidate?.phone || candidate?.phone_number) && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Phone
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon sx={{ fontSize: "18px", color: "#6B7280" }} />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#101828",
                    fontSize: "15px",
                  }}
                >
                  {candidate.phone || candidate.phone_number}
                </Typography>
              </Box>
            </Box>
          )}

          {candidate?.matchScore && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Match Score
              </Typography>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#101828",
                      fontSize: "15px",
                    }}
                  >
                    {candidate.matchScore}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={candidate.matchScore}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#3B82F6",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {(candidate?.linkedinUrl || candidate?.linkedin_url) && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                LinkedIn Profile
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LinkedInIcon sx={{ fontSize: "18px", color: "#0077B5" }} />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#0077B5",
                    fontSize: "15px",
                    textDecoration: "none",
                    wordBreak: "break-all",
                  }}
                  component="a"
                  href={candidate.linkedinUrl || candidate.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {candidate.linkedinUrl || candidate.linkedin_url}
                </Typography>
              </Box>
            </Box>
          )}

          {(candidate?.replyText || candidate?.reply_text) && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Reply Text
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#101828",
                  fontSize: "15px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {candidate.replyText || candidate.reply_text}
              </Typography>
            </Box>
          )}

          {candidate?.classification && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6B7280",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Classification
              </Typography>
              <Chip
                label={candidate.classification}
                size="small"
                sx={{
                  backgroundColor: "#F3F4F6",
                  color: "#101828",
                  fontWeight: 500,
                  fontSize: "13px",
                  height: "24px",
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "#3B82F6",
            "&:hover": {
              backgroundColor: "#2563EB",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LeadBoard = () => {
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [createCampaignDialogOpen, setCreateCampaignDialogOpen] =
    useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [updatingCampaignStatus, setUpdatingCampaignStatus] = useState(false);
  const [manualEmployeeIds, setManualEmployeeIds] = useState("");
  const [selectedApolloIds, setSelectedApolloIds] = useState([]);
  const [callConfirmationOpen, setCallConfirmationOpen] = useState(false);
  const [candidateToCall, setCandidateToCall] = useState(null);
  const [makingCall, setMakingCall] = useState(false);
  const [callError, setCallError] = useState(null);
  const [callSuccess, setCallSuccess] = useState(null);
  const [linkedInMessageModalOpen, setLinkedInMessageModalOpen] =
    useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [aiMessage, setAiMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Pipeline data from API
  const [pipelineData, setPipelineData] = useState({
    queued: [],
    outreached: [],
    opened: [],
    shortlisted: [],
    converted: [],
  });

  // Map API status to column ID - Only these 5 statuses are supported
  const statusToColumnMap = {
    QUEUED: "queued",
    OUTREACHED: "outreached",
    OPENED: "opened",
    SHORTLISTED: "shortlisted",
    CONVERTED: "converted",
  };

  // Fetch all outreaches and populate pipeline
  const fetchAllOutreaches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await smartleadAPI.getOutreachesByStatusAll();

      // Process the response and organize by status
      const newPipelineData = {
        queued: [],
        outreached: [],
        opened: [],
        shortlisted: [],
        converted: [],
      };

      // Handle the actual API response structure: { success: true, data: { QUEUED: [...], OUTREACHED: [...], ... } }
      if (response && response.success && response.data) {
        const data = response.data;

        // Process each status in the data object - Only process the 5 supported statuses
        Object.keys(data).forEach((status) => {
          const columnId = statusToColumnMap[status.toUpperCase()];

          // Only process if status matches one of the 5 supported statuses
          if (columnId && Array.isArray(data[status])) {
            const employees = data[status].map((employee) => ({
              id: employee.employee_id,
              apolloId: employee.apollo_id,
              name: employee.full_name || employee.first_name || "Unknown",
              title: employee.title || "",
              batch: employee.organization_name || "",
              matchScore: null,
              email: employee.email || "",
              linkedinUrl: employee.linkedin_url || "",
              domain: employee.domain || "",
              replyText: employee.reply_text || "",
              classification: employee.classification || "",
              outreachId: employee.outreach_id || employee.employee_id,
              phone: employee.phone || employee.phone_number || "",
              organizationId:
                employee.organization_id || employee.organisation_id || "",
            }));

            // If multiple statuses map to the same column, merge them
            if (newPipelineData[columnId]) {
              newPipelineData[columnId] = [
                ...newPipelineData[columnId],
                ...employees,
              ];
            } else {
              newPipelineData[columnId] = employees;
            }
          }
        });
      }

      setPipelineData(newPipelineData);

      // Update available employees from outreaches
      const allEmployees = Object.values(newPipelineData)
        .flat()
        .filter(
          (emp, index, self) => index === self.findIndex((e) => e.id === emp.id)
        )
        .map((emp) => ({
          employee_id: emp.id,
          full_name: emp.name,
          email: emp.email,
        }));
      setAvailableEmployees(allEmployees);
    } catch (error) {
      console.error("Error fetching outreaches:", error);
      setError(
        "Failed to load outreaches. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Load campaigns from localStorage (since there's no GET campaigns API)
  useEffect(() => {
    const storedCampaigns = localStorage.getItem("smartlead_campaigns");
    if (storedCampaigns) {
      try {
        setCampaigns(JSON.parse(storedCampaigns));
      } catch (e) {
        console.error("Error parsing stored campaigns:", e);
      }
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchAllOutreaches();
  }, []);

  const handlePhoneClick = (candidate) => {
    console.log("Phone clicked for candidate:", candidate);
    setCandidateToCall(candidate);
    setCallConfirmationOpen(true);
    setCallError(null);
    setCallSuccess(null);
  };

  const handleConfirmCall = async () => {
    if (!candidateToCall) return;

    if (!candidateToCall.phone) {
      setCallError("Phone number not available for this candidate");
      return;
    }

    setMakingCall(true);
    setCallError(null);
    setCallSuccess(null);

    try {
      await voiceAgentAPI.makeCall(
        candidateToCall.phone,
        candidateToCall.apolloId, // person_id
        candidateToCall.organizationId // organisation_id
      );
      setCallSuccess("Call initiated successfully!");
      setCallConfirmationOpen(false);
      setTimeout(() => {
        setCallSuccess(null);
        setCandidateToCall(null);
      }, 2000);
    } catch (error) {
      setCallError(
        error.response?.data?.message ||
          error.message ||
          "Failed to initiate call. Please try again."
      );
    } finally {
      setMakingCall(false);
    }
  };

  const handleCloseCallConfirmation = () => {
    if (!makingCall) {
      setCallConfirmationOpen(false);
      setCandidateToCall(null);
      setCallError(null);
      setCallSuccess(null);
    }
  };

  const handleCardClick = (candidate) => {
    setSelectedCandidate(candidate);
    setModalOpen(true);
  };

  const handleSaveCard = (cardData) => {
    // Update local state only
    setPipelineData((prev) => {
      const updated = { ...prev };
      for (const columnId in updated) {
        const index = updated[columnId].findIndex((c) => c.id === cardData.id);
        if (index !== -1) {
          updated[columnId][index] = cardData;
          break;
        }
      }
      return updated;
    });
  };

  // LinkedIn Message handlers
  const handleGenerateMessage = async () => {
    if (!userPrompt.trim()) {
      setError("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
    } catch (error) {
      console.error("Error generating message:", error);
      setError("Failed to generate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateMessage = async () => {
    if (!userPrompt.trim()) {
      setError("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
    } catch (error) {
      console.error("Error regenerating message:", error);
      setError("Failed to regenerate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendLinkedInMessages = async () => {
    if (!aiMessage.trim()) {
      setError("Please generate a message first");
      return;
    }

    if (selectedApolloIds.length === 0) {
      setError("No candidates selected to send messages to");
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      // Get all selected candidates from all columns
      const allCandidates = Object.values(pipelineData).flat();
      const selectedCandidates = allCandidates.filter((candidate) =>
        selectedApolloIds.includes(candidate.apolloId)
      );

      const candidatesWithLinkedIn = selectedCandidates.filter(
        (candidate) => candidate.linkedinUrl
      );

      if (candidatesWithLinkedIn.length === 0) {
        setError("No candidates with LinkedIn profiles found");
        setIsSending(false);
        return;
      }

      // Collect all LinkedIn URLs
      const linkedInUrls = candidatesWithLinkedIn.map(
        (candidate) => candidate.linkedinUrl
      );

      // Send messages to all candidates with LinkedIn profiles in one API call
      await linkedinAPI.sendMessage(linkedInUrls, aiMessage);

      const successCount = linkedInUrls.length;
      setError(null);
      setLinkedInMessageModalOpen(false);
      setUserPrompt("");
      setAiMessage("");
      setSelectedApolloIds([]);
      alert(`Successfully sent ${successCount} message(s)!`);
    } catch (error) {
      console.error("Error sending messages:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while sending messages"
      );
    } finally {
      setIsSending(false);
    }
  };

  // Handle checkbox change for candidates
  const handleCheckboxChange = (apolloId, checked) => {
    if (checked) {
      setSelectedApolloIds((prev) => [...prev, apolloId]);
    } else {
      setSelectedApolloIds((prev) => prev.filter((id) => id !== apolloId));
    }
  };

  // Send emails (create campaign)
  const handleSendEmails = async () => {
    if (selectedApolloIds.length === 0) {
      setError("Please select at least one candidate");
      return;
    }

    setCreatingCampaign(true);
    setError(null);
    try {
      // Get employee_ids (integers) for selected apollo IDs
      const allCandidates = Object.values(pipelineData).flat();
      const selectedCandidates = allCandidates.filter((candidate) =>
        selectedApolloIds.includes(candidate.apolloId)
      );

      // Extract employee_ids (integers) from selected candidates
      const employeeIds = selectedCandidates
        .map((candidate) => candidate.id)
        .filter((id) => id != null)
        .map((id) => {
          // Convert to integer if it's a string
          const intId = typeof id === "string" ? parseInt(id, 10) : id;
          return isNaN(intId) ? null : intId;
        })
        .filter((id) => id !== null);

      if (employeeIds.length === 0) {
        setError("No valid employee IDs found for selected candidates");
        setCreatingCampaign(false);
        return;
      }

      const response = await smartleadAPI.createCampaign(
        employeeIds,
        "Send Emails"
      );

      if (response.success) {
        // Add campaign to local storage
        const newCampaign = {
          campaign_id: response.campaign_id,
          smartlead_campaign_id: response.smartlead_campaign_id,
          name: response.name,
          status: response.status,
          total_leads: response.total_leads,
        };
        const updatedCampaigns = [...campaigns, newCampaign];
        setCampaigns(updatedCampaigns);
        localStorage.setItem(
          "smartlead_campaigns",
          JSON.stringify(updatedCampaigns)
        );

        // Reset selection
        setSelectedApolloIds([]);
        setError(null);

        // Refresh data
        await fetchAllOutreaches();
      }
    } catch (error) {
      setError(
        `Failed to send emails: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`
      );
      console.error("Error sending emails:", error);
    } finally {
      setCreatingCampaign(false);
    }
  };

  // Update campaign status
  const handleUpdateCampaignStatus = async (campaignId, status) => {
    setUpdatingCampaignStatus(true);
    setError(null);
    try {
      const response = await smartleadAPI.updateCampaignStatus(
        campaignId,
        status
      );

      if (response.success) {
        // Update campaign in local storage
        const updatedCampaigns = campaigns.map((campaign) =>
          campaign.campaign_id === campaignId
            ? { ...campaign, status: response.status }
            : campaign
        );
        setCampaigns(updatedCampaigns);
        localStorage.setItem(
          "smartlead_campaigns",
          JSON.stringify(updatedCampaigns)
        );

        // If this is the selected campaign, refresh data
        if (selectedCampaign === campaignId) {
          await fetchAllOutreaches();
        }
      }
    } catch (error) {
      setError(
        `Failed to update campaign status: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`
      );
      console.error("Error updating campaign status:", error);
    } finally {
      setUpdatingCampaignStatus(false);
    }
  };

  // Fetch available employees from outreaches
  const fetchAvailableEmployees = async () => {
    try {
      // Get unique employees from all outreaches
      const allEmployees = Object.values(pipelineData)
        .flat()
        .filter(
          (emp, index, self) => index === self.findIndex((e) => e.id === emp.id)
        )
        .map((emp) => ({
          employee_id: emp.id,
          full_name: emp.name,
          email: emp.email,
        }));
      setAvailableEmployees(allEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  // Calculate summary counts (use campaign stats if available)
  const summaryData = [
    {
      icon: PersonIcon,
      count: pipelineData["queued"].length,
      label: "Queued",
      color: "#6B7280",
    },
    {
      icon: EmailIcon,
      count: pipelineData["outreached"].length,
      label: "Outreached",
      color: "#9333EA",
    },
    {
      icon: MarkEmailReadIcon,
      count: campaignStats?.total_opened || pipelineData["opened"].length,
      label: "Opened Email",
      color: "#F59E0B",
    },
    {
      icon: PersonIcon,
      count: pipelineData["shortlisted"].length,
      label: "Shortlisted",
      color: "#3B82F6",
    },
    {
      icon: CheckCircleIcon,
      count: pipelineData["converted"].length,
      label: "Leads Converted",
      color: "#10B981",
    },
  ];

  const columns = [
    {
      id: "shortlisted",
      title: "Shortlisted",
      icon: PersonIcon,
      color: "#3B82F6",
      candidates: pipelineData["shortlisted"],
    },
    {
      id: "queued",
      title: "Queued",
      icon: PersonIcon,
      color: "#6B7280",
      candidates: pipelineData["queued"],
    },
    {
      id: "outreached",
      title: "Outreached",
      icon: EmailIcon,
      color: "#9333EA",
      candidates: pipelineData["outreached"],
    },
    {
      id: "opened",
      title: "Opened Email",
      icon: MarkEmailReadIcon,
      color: "#F59E0B",
      candidates: pipelineData["opened"],
    },
    {
      id: "converted",
      title: "Leads Converted",
      icon: CheckCircleIcon,
      color: "#10B981",
      candidates: pipelineData["converted"],
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Scrollable Container - Header and Content together */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
          paddingBottom: { 
            xs: "calc(60px + env(safe-area-inset-bottom, 0px))", 
            sm: "calc(40px + env(safe-area-inset-bottom, 0px))", 
            md: "0px" 
          },
          WebkitOverflowScrolling: "touch",
          marginBottom: { xs: "0px", md: "0px" },
        }}
      >
        {/* Header */}
        <Box sx={{ padding: { xs: "16px 16px 0 16px", sm: "24px 24px 0 24px" } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "flex-start" },
              mb: 2,
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: "20px", sm: "22px", md: "24px" },
                  color: "#101828",
                }}
              >
                Lead Pipeline Board
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  color: "#6B7280", 
                  fontSize: { xs: "13px", sm: "14px", md: "16px" } 
                }}
              >
                Track decision makers through different stages of your
                recruitment pipeline.
              </Typography>
            </Box>
            <Box sx={{ 
              display: "flex", 
              gap: { xs: 1, sm: 2 }, 
              alignItems: "center",
              flexWrap: "wrap",
              width: { xs: "100%", sm: "auto" },
            }}>
              {selectedApolloIds.length > 0 && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CampaignIcon />}
                    onClick={handleSendEmails}
                    disabled={creatingCampaign}
                    sx={{
                      backgroundColor: "#3B82F6",
                      "&:hover": { backgroundColor: "#2563EB" },
                    }}
                  >
                    {creatingCampaign ? "Sending..." : "Send Emails"}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<LinkedInIcon />}
                    onClick={() => setLinkedInMessageModalOpen(true)}
                    disabled={isSending}
                    sx={{
                      backgroundColor: "#0077B5",
                      "&:hover": { backgroundColor: "#005885" },
                    }}
                  >
                    Send LinkedIn Message ({selectedApolloIds.length})
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              flex: 1,
              maxHeight: "calc(100vh + 124px)",
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D1D5DB",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#9CA3AF",
                },
              },
            }}
          >
            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            {/* Loading State */}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Kanban Board */}
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    overflowX: "auto",
                    overflowY: "auto",
                    pb: 2,
                    flex: 1,
                    minHeight: 0,
                    "&::-webkit-scrollbar": {
                      height: "8px",
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#F3F4F6",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#D1D5DB",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#9CA3AF",
                      },
                    },
                  }}
                >
                  {columns.map((column) => (
                    <PipelineColumn
                      key={column.id}
                      id={column.id}
                      title={column.title}
                      icon={column.icon}
                      color={column.color}
                      candidates={column.candidates}
                      showCheckbox={true}
                      selectedApolloIds={selectedApolloIds}
                      onCheckboxChange={handleCheckboxChange}
                      onPhoneClick={handlePhoneClick}
                      onCardClick={handleCardClick}
                    />
                  ))}
                </Box>

                {/* Pipeline Summary */}
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { xs: "16px", sm: "18px" },
                      color: "#101828",
                    }}
                  >
                    Pipeline Summary
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {summaryData.map((summary, index) => (
                      <Grid item xs={6} sm={4} md={2.4} key={index}>
                        <SummaryCard {...summary} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Card Details Modal */}
      <CardDetailsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCandidate(null);
        }}
        candidate={selectedCandidate}
        onSave={handleSaveCard}
        viewOnly={true}
      />

      {/* Call Confirmation Modal */}
      <Dialog
        open={callConfirmationOpen}
        onClose={handleCloseCallConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Call</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {callError && (
              <Alert severity="error" onClose={() => setCallError(null)}>
                {callError}
              </Alert>
            )}
            {callSuccess && (
              <Alert severity="success" onClose={() => setCallSuccess(null)}>
                {callSuccess}
              </Alert>
            )}
            {candidateToCall && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Are you sure you want to call {candidateToCall.name}?
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    <strong>Phone:</strong> {candidateToCall.phone}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    <strong>Title:</strong> {candidateToCall.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    <strong>Company:</strong> {candidateToCall.batch}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCallConfirmation}
            disabled={makingCall}
            sx={{ color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCall}
            variant="contained"
            disabled={makingCall}
            startIcon={
              makingCall ? <CircularProgress size={20} /> : <PhoneIcon />
            }
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": {
                backgroundColor: "#2563EB",
              },
            }}
          >
            {makingCall ? "Initiating..." : "Make Call"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* LinkedIn Message Modal */}
      <Dialog
        open={linkedInMessageModalOpen}
        onClose={() => {
          if (!isSending && !isGenerating) {
            setLinkedInMessageModalOpen(false);
            setUserPrompt("");
            setAiMessage("");
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Send LinkedIn Message</Typography>
            <IconButton
              onClick={() => {
                if (!isSending && !isGenerating) {
                  setLinkedInMessageModalOpen(false);
                  setUserPrompt("");
                  setAiMessage("");
                }
              }}
              disabled={isSending || isGenerating}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Selected: {selectedApolloIds.length} candidate(s)
            </Typography>

            <TextField
              label="Message Prompt"
              placeholder="e.g., Write a professional LinkedIn message for a software engineer candidate with 5 years of experience in Python and React. Mention that we have a remote position with competitive salary."
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={isGenerating || isSending}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleGenerateMessage}
                disabled={!userPrompt.trim() || isGenerating || isSending}
                startIcon={isGenerating ? <CircularProgress size={20} /> : null}
              >
                {isGenerating ? "Generating..." : "Generate Message"}
              </Button>
              {aiMessage && (
                <Button
                  variant="outlined"
                  onClick={handleRegenerateMessage}
                  disabled={!userPrompt.trim() || isGenerating || isSending}
                  startIcon={
                    isGenerating ? <CircularProgress size={20} /> : null
                  }
                >
                  {isGenerating ? "Regenerating..." : "Regenerate"}
                </Button>
              )}
            </Box>

            {aiMessage && (
              <TextField
                label="Generated Message"
                value={aiMessage}
                onChange={(e) => setAiMessage(e.target.value)}
                fullWidth
                multiline
                rows={8}
                disabled={isSending}
                helperText="You can edit the message above before sending"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!isSending && !isGenerating) {
                setLinkedInMessageModalOpen(false);
                setUserPrompt("");
                setAiMessage("");
              }
            }}
            disabled={isSending || isGenerating}
            sx={{ color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendLinkedInMessages}
            disabled={
              !aiMessage.trim() || isSending || selectedApolloIds.length === 0
            }
            startIcon={
              isSending ? <CircularProgress size={20} /> : <LinkedInIcon />
            }
            sx={{
              backgroundColor: "#0077B5",
              "&:hover": {
                backgroundColor: "#005885",
              },
              "&:disabled": {
                backgroundColor: "#93C5FD",
              },
            }}
          >
            {isSending ? "Sending..." : "Send Messages"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadBoard;
